import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';

import { AuthClient } from './apiClient'; // Adjust the import path
import { API_BASE_URL } from '../config/config'; // Adjust the import path
import {
  isTokenExpiringSoon,
  getToken,
  getRefreshToken,
  setTokens,
  clearAllTokens,
  clearImpersonationTokens,
  isImpersonating,
} from '../utils/tokenUtils'; // Import token utilities

/**
 * Extend AxiosRequestConfig to include a custom _retry property.
 */
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Shared variables for token refresh logic.
 */
let isRefreshing = false;
let refreshTokenPromise: Promise<string | null> | null = null; // Promise to resolve the new access token
const requestQueue: Array<(newToken: string | null) => void> = []; // Queue for pending requests

/**
 * Auth endpoints to skip refresh token logic.
 */
const AUTH_ENDPOINTS = [
  '/auth/token',
];

/**
 * Axios instance with interceptors.
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  transformResponse: [],
});

/**
 * Request Interceptor - with proactive token refresh
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    const accessToken = getToken();
    const refreshToken = getRefreshToken();

    // Proactive token refresh: Check if token is expiring soon (within 60 seconds)
    if (accessToken && refreshToken && isTokenExpiringSoon(60)) {
      // Skip proactive refresh for auth endpoints
      const isAuthEndpoint = config.url &&
        AUTH_ENDPOINTS.some(endpoint => config.url?.includes(endpoint));

      if (!isAuthEndpoint) {
        if (!isRefreshing) {
          // Start token refresh process proactively
          isRefreshing = true;
          refreshTokenPromise = refreshTokenFlow(refreshToken);

          try {
            const newToken = await refreshTokenPromise;
            if (newToken && config.headers) {
              config.headers.Authorization = `Bearer ${newToken}`;
            }
          } catch (error) {
            console.error('Proactive token refresh failed:', error);
            // Continue with existing token, will be handled by response interceptor if it fails
          } finally {
            isRefreshing = false;
            refreshTokenPromise = null;
          }
        } else if (refreshTokenPromise) {
          // Wait for ongoing refresh
          try {
            const newToken = await refreshTokenPromise;
            if (newToken && config.headers) {
              config.headers.Authorization = `Bearer ${newToken}`;
            }
          } catch (error) {
            console.error('Waiting for token refresh failed:', error);
          }
        }
      }
    } else if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response Interceptor
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    
    // Skip refresh token logic for auth endpoints
    const isAuthEndpoint = originalRequest?.url && 
      AUTH_ENDPOINTS.some(endpoint => originalRequest.url?.includes(endpoint));

    // Handle 401 Unauthorized responses, but skip for auth endpoints
    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint) {
      originalRequest._retry = true; // Mark the request as retried

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        navigateToLogin();
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        // Start token refresh process
        isRefreshing = true;
        refreshTokenPromise = refreshTokenFlow(refreshToken);
        refreshTokenPromise.finally(() => {
          isRefreshing = false;
          refreshTokenPromise = null;
        });
      }

      // Wait for refresh token promise to resolve or reject
      return new Promise((resolve, reject) => {
        requestQueue.push((newAccessToken) => {
          if (newAccessToken) {
            // Retry the original request with the new token
            if (originalRequest?.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            }
            resolve(axiosInstance(originalRequest));
          } else {
            // Refresh failed; reject the request
            reject(error);
          }
        });
      });
    }

    return Promise.reject(error);
  },
);

/**
 * Handles the refresh token process and retries pending requests.
 */
async function refreshTokenFlow(refreshToken: string): Promise<string | null> {
  const tokenClient = new AuthClient(API_BASE_URL, axiosInstance);

  try {
    const { data } = await tokenClient.refreshToken({ refreshToken });
    const newAccessToken = data?.accessToken;
    const newRefreshToken = data?.refreshToken;

    // Update tokens in the appropriate storage (automatically handles impersonation)
    setTokens(newAccessToken ?? null, newRefreshToken ?? null);

    // Resolve all queued requests with the new access token
    requestQueue.forEach((callback) => callback(newAccessToken ?? ''));
    requestQueue.length = 0; // Clear the queue

    return newAccessToken ?? '';
  } catch (error) {
    console.error('Token refresh failed:', error);
    // If refresh token API fails, clear tokens and navigate to login
    navigateToLogin();

    // Reject all queued requests
    requestQueue.forEach((callback) => callback(null));
    requestQueue.length = 0; // Clear the queue

    return null;
  }
}

/**
 * Function to navigate to the login page, or back to the impersonation entry point if
 * the session that expired was an impersonation session.
 */
const navigateToLogin = (): void => {
  if (isImpersonating()) {
    // Clear only the impersonation session — admin tokens in localStorage remain intact
    clearImpersonationTokens();
    const errorMsg = encodeURIComponent('This session is no longer active and cannot be accessed.');
    window.location.href = `/login?error=${errorMsg}`;
    return;
  }

  clearAllTokens();
  window.location.href = '/login';
};

export default axiosInstance;
